import React, { useEffect, useRef, useState } from 'react';
import TriageWorkbench from '../common/triage/TriageWorkbench';
import { LinkedCapa, LinkedTask, TriageSubject } from '../common/triage/types';
import { Anomaly } from '../file_analytics/types';
import FileAnalyticsAPI from '../file_analytics/fileAnalyticsService';
import { capaService } from '../../services/capaService';
import { tasksService } from '../../services/tasksService';
import { userService } from '../../services/userService';
import { useCanTriageEvents } from '../file_analytics/permissions';

interface Props {
    isOpen: boolean;
    anomaly: Anomaly | null;
    onClose: () => void;
    onUpdated: () => void;
}

const AnomalyTriageModal: React.FC<Props> = ({ isOpen, anomaly, onClose, onUpdated }) => {
    const canTriage = useCanTriageEvents();
    const [ownerOptions, setOwnerOptions] = useState<{ value: number; label: string }[]>([]);
    const [capa, setCapa] = useState<LinkedCapa | null>(null);
    const [task, setTask] = useState<LinkedTask | null>(null);
    const [linkLoading, setLinkLoading] = useState(false);

    // Preserve the last valid anomaly so modal body stays populated during the close animation.
    const lastRef = useRef<Anomaly | null>(anomaly);
    if (anomaly) lastRef.current = anomaly;
    const a = lastRef.current;

    useEffect(() => {
        if (!isOpen) return;
        userService.getUsersDropdown()
            .then(us => setOwnerOptions(us.map(u => ({ value: u.id, label: u.name }))))
            .catch(() => setOwnerOptions([]));
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !a) { setCapa(null); setTask(null); return; }
        let cancelled = false;
        setLinkLoading(true);
        Promise.allSettled([
            capaService.list({ source_type: 'Continuous Monitoring' }),
            tasksService.list({ source_type_code: 'CMG_ANOMALY', source_record_id: a.id }),
        ]).then(([capaRes, taskRes]) => {
            if (cancelled) return;
            if (capaRes.status === 'fulfilled') {
                const match = capaRes.value
                    .filter(r => r.source_record_id === a.id)
                    .sort((x, y) => y.id - x.id)[0];
                setCapa(match ? {
                    id: match.id,
                    issue_code: match.issue_code,
                    issue_name: match.issue_name,
                    is_resolved: !!match.is_resolved,
                    issue_head_name: match.issue_head_name ?? undefined,
                    open_action_count: match.open_action_count,
                    action_count: match.action_count,
                } : null);
            }
            if (taskRes.status === 'fulfilled') {
                const t = taskRes.value[0];
                setTask(t ? {
                    id: t.id,
                    name: t.name,
                    status: t.status,
                    doer_name: t.doer_name ?? undefined,
                    due_date: t.due_date ?? undefined,
                } : null);
            }
        }).finally(() => { if (!cancelled) setLinkLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, a]);

    const subject: TriageSubject | null = a ? {
        code: a.record_key || `#${a.id}`,
        title: `Triage Anomaly — ${a.rule_name ?? `#${a.id}`}`,
        status: a.status,
        canTriage,
        permissionHint: 'You do not have permission to triage anomalies. Contact your administrator.',
        panelHeading: 'Anomaly',
        details: [
            { label: 'Rule', value: a.rule_name ?? `Rule #${a.rule_id}` },
            ...(a.pipeline_name ? [{ label: 'Pipeline', value: a.pipeline_name }] : []),
            { label: 'Linked risk', value: a.risk_code ? `${a.risk_code}${a.risk_name ? ` — ${a.risk_name}` : ''}` : 'Unlinked' },
            { label: 'Detected', value: a.created_on ?? '—' },
        ],
        recordSnapshot: a.record_snapshot ?? null,
        linked: { task, capa, taskLoading: linkLoading, capaLoading: linkLoading },
        onTrivial: async p => {
            await FileAnalyticsAPI.triageAnomalyDecision(a.id, {
                decision: 'trivial',
                reporter_feedback: `Reason: ${p.reason}. ${p.feedback}`.trim(),
            });
        },
        onEscalate: async p => {
            await FileAnalyticsAPI.triageAnomalyDecision(a.id, {
                decision: 'escalate',
                risk_severity: p.risk_severity,
                capa_owner_id: p.capa_owner_id,
                instructions: p.instructions,
                target_date: p.target_date,
            });
        },
        onTask: async p => {
            await FileAnalyticsAPI.triageAnomalyDecision(a.id, {
                decision: 'task',
                task_title: p.task_title,
                task_description: p.task_description,
                task_assignee_id: p.task_assignee_id,
                task_approver_id: p.task_approver_id,
                task_validator_id: p.task_validator_id,
                task_due_date: p.task_due_date,
                task_grace_period_days: p.task_grace_period_days,
                task_priority: p.task_priority,
            });
        },
    } : null;

    return (
        <TriageWorkbench
            isOpen={isOpen}
            subject={subject}
            ownerOptions={ownerOptions}
            onClose={onClose}
            onUpdated={onUpdated}
        />
    );
};

export default AnomalyTriageModal;
